import { LegalPage, LegalProse, LegalSection } from '../components/LegalProse';

/**
 * /delete-account — the public account-deletion instructions.
 *
 * Exists for the Play Console Data safety "Delete account URL" field and the
 * equivalent store requirement: a page reachable WITHOUT the app and without
 * signing in, that names the app and developer, walks the deletion steps, and
 * says what is erased, what outlives the account, and how long the window is.
 * /privacy carries the legal right (§6 Request for Deletion) but no steps, and
 * /settings is behind a login — neither answers that field.
 *
 * Every fact here is the edge's own behaviour, not a restatement of policy:
 *   steps        → the Delete account modal, POST /api/account/delete
 *                  (src/edge/routes/web-session-routes.js) and the same door in
 *                  the two apps' Settings screens
 *   30 days      → ACCOUNT_DELETION_RETENTION_DAYS, and the pending_deletion
 *                  window described in src/edge/account-deletion/account-deletion.js
 *   restore      → explicit only (2026-08-21): the web restore screen, or the
 *                  RESTORE text (isRestoreCommand)
 *   what is gone → store.deletePerson's table list, plus the Cloudinary assets
 *                  its callers destroy
 *   what is kept → COMPLIANCE_EVENT_TYPES (deletePersonEvents) and the
 *                  deidentified public Request (migration 134)
 * Change the code and this page changes with it.
 */
export default function DeleteAccount() {
  return (
    <LegalPage title="Delete your Ember account" dateline="Ember is a service of Oriscen, Inc.">

        <LegalProse>
          <p>
            You can delete your Ember account yourself, from the Ember app for Android or iPhone or
            from the website. You do not need to ask us, and nothing has to be emailed anywhere.
          </p>

          <LegalSection title="How to delete your account">
            <ol>
              <li>
                Open Ember &mdash; the Android app, the iPhone app, or{' '}
                <a href="https://ember.space/">ember.space</a> &mdash; and sign in.
              </li>
              <li>
                Open <strong>Settings</strong>. In the apps it is in the menu on the home screen and
                at the bottom of Your Embers. On the web it is in the account menu, at{' '}
                <a href="https://ember.space/settings">ember.space/settings</a>.
              </li>
              <li>
                Scroll to the bottom and tap <strong>Delete account</strong>.
              </li>
              <li>
                Confirm in the dialog. The deletion is scheduled the moment you confirm; there is no
                further step.
              </li>
            </ol>
          </LegalSection>

          <LegalSection title="What happens after you confirm">
            <p>
              Service stops immediately. You are signed out everywhere, your Embers are no longer
              shown to you or to anyone else, nothing further can be written to the account, and
              Ember stops texting you.
            </p>
            <p>
              <strong>Thirty days later everything below is erased permanently.</strong> Those 30
              days are the only additional retention period, and they exist for one reason: so a
              deletion made by mistake can still be undone.
            </p>
            <p>
              To undo it within the 30 days, sign in again &mdash; you will land on the restore
              screen, which is the only way back &mdash; or reply <strong>RESTORE</strong> to a text
              from Ember. After the 30 days nothing can be recovered, by you or by us.
            </p>
          </LegalSection>

          <LegalSection title="What is deleted">
            <p>Everything the account holds, including:</p>
            <ul>
              <li>your phone number and every other sign-in identity</li>
              <li>
                every Ember, and the whole conversation behind it &mdash; typed messages, voice-call
                transcripts, and unfinished drafts
              </li>
              <li>photos and videos you added, from our media host as well as from our database</li>
              <li>
                the recordings and transcripts of your calls held by our voice provider, deleted
                there as well as here
              </li>
              <li>the thank-yous and notes you left, and the ones left on your Embers</li>
              <li>
                your Meet people cards and requests, what you wrote in one-on-one chats and in
                rooms, and the reminders you asked Ember for
              </li>
              <li>your notification settings and your devices&rsquo; push tokens</li>
              <li>the product-analytics events attributed to you</li>
            </ul>
          </LegalSection>

          <LegalSection title="What is kept, and why">
            <p>Three things outlive the account. None of them contains what you told Ember.</p>
            <p>
              <strong>Compliance records.</strong> A minimal trail of the promises we have to be able
              to prove we kept: your opt-in and opt-out of messaging, the consent decisions you made,
              terms and safety decisions (including that crisis resources were placed in front of
              you, without your words), and proof that this deletion took place. No Ember content
              is in it.
            </p>
            <p>
              <strong>A public question you asked, without you.</strong> If you once published a
              Request and other people answered it with their Embers, the question stays, with your
              account and the conversation it came from cut away from it. Deleting the question
              would delete their answers, and a right over your own account is not a right over
              theirs.
            </p>
            <p>
              <strong>A message you sent the team.</strong> What you wrote to us through Feedback
              stays with us as a message; the email address you left with it is removed.
            </p>
          </LegalSection>

          <LegalSection title="Deleting only part of your data">
            <p>
              You do not have to close the account to remove something in it. In Your Embers, open an
              Ember and delete it: that Ember leaves every place it appeared — what helps Ember
              understand similar situations, any reader you had said yes to, the lights it stood at — and the rest of
              the account is untouched. The conversation it was written from stays in your account,
              as your own record, until you delete the account; if you want a particular
              conversation or part of one gone sooner, email{' '}
              <a href="mailto:contact@oriscen.ai">contact@oriscen.ai</a> and we will remove it.
              If you once said yes to one person reading an Ember, its own page lets you close it
              again, without deleting anything.
            </p>
          </LegalSection>

          <LegalSection title="If you cannot sign in">
            <p>
              If you no longer have the phone number on the account, email{' '}
              <a href="mailto:contact@oriscen.ai">contact@oriscen.ai</a>. We will confirm who you are
              before deleting anything, and then run the same deletion described above.
            </p>
          </LegalSection>
        </LegalProse>
    </LegalPage>
  );
}
